import React, { useState, useEffect } from 'react';
import { Shield, X, Loader2, MapPin, Globe, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface FormData {
  club_name: string;
  description: string;
  city: string;
  formatted_address: string;
  formatted_phone_number: string;
  website: string;
  logo: File | null;
}

interface CreateClubModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateClubModal: React.FC<CreateClubModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    club_name: '',
    description: '',
    city: '',
    formatted_address: '',
    formatted_phone_number: '',
    website: '',
    logo: null as File | null
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        club_name: '',
        description: '',
        city: '',
        formatted_address: '',
        formatted_phone_number: '',
        website: '',
        logo: null
      });
      setLogoPreview(null);
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, logo: 'Please upload a JPEG, PNG, or GIF image' }));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logo: 'Image size must be less than 5MB' }));
      return;
    }

    setFormData(prev => ({ ...prev, logo: file }));
    setLogoPreview(URL.createObjectURL(file));
    
    // Clear error for logo if it exists
    if (errors.logo) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.logo;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.club_name.trim()) {
      newErrors.club_name = 'Club name is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    // Validate website format if provided
    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }
    
    // Validate phone format if provided
    if (formData.formatted_phone_number.trim() && !isValidPhone(formData.formatted_phone_number)) {
      newErrors.formatted_phone_number = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true; 
    } catch {
      return false;
    }
  };

  const isValidPhone = (phone: string) => {
    return /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(phone);
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, logo: file });
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setIsSubmitting(true);
      
      // Get current user's auth ID
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!currentUser) throw new Error('Not authenticated');

      // Get user's database ID
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', currentUser.id)
        .single();
      
      if (userDataError) throw userDataError;
      if (!userData) throw new Error('User not found');
      
      // Generate a unique club ID
      const clubId = crypto.randomUUID();
      
      // Upload logo if provided (using user directory structure)
      let logoUrl = null;
      if (formData.logo) {
        const filePath = `club_logos/${currentUser.id}/${Date.now()}.${formData.logo.name.split('.').pop()}`;
        
        const { error: uploadError } = await supabase.storage
          .from('clubs')
          .upload(filePath, formData.logo);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('clubs')
          .getPublicUrl(filePath);
        
        logoUrl = publicUrl;
      }
      
      // Create club record
      const { data: clubData, error: clubError } = await supabase
        .from('clubs')
        .insert({ 
          club_id: clubId,
          club_name: formData.club_name,
          description: formData.description || null,
          city: formData.city,
          formatted_address: formData.formatted_address,
          formatted_phone_number: formData.formatted_phone_number || null,
          website: formData.website || null,
          logo: logoUrl,
          created_by: currentUser.id
        })
        .select('club_id')
        .single();
      
      if (clubError) throw clubError;
      
      // Add user as club member and admin
      const { error: membershipError } = await supabase
        .from('user_clubs')
        .insert({
          user_id: userData.id,
          club_id: clubId,
          is_primary: true,
          is_admin: true
        });
      
      if (membershipError) throw membershipError;
      
      // Set as user's primary club
      const { error: updateUserError } = await supabase
        .from('users')
        .update({ club_id: clubId })
        .eq('id', userData.id);
      
      if (updateUserError) throw updateUserError;
      
      toast({
        title: "Club Created",
        description: "Your club has been created successfully!",
        variant: "default"
      });
      
      onSuccess();
    } catch (error) {
      console.error("Error creating club:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create club",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] bg-dark-200 border-dark-300 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Shield className="h-5 w-5 text-gold-400 mr-2" />
            Create New Club
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Club Logo */}
          <div className="flex flex-col items-center">
            <div 
              className={cn(
                "w-24 h-24 rounded-full flex items-center justify-center cursor-pointer overflow-hidden",
                "border-2 border-dashed",
                errors.logo ? "border-red-500" : "border-dark-400 hover:border-gold-400",
                "transition-colors"
              )}
              onClick={() => document.getElementById('club-logo-input')?.click()}
            >
              {logoPreview ? (
                <img 
                  src={logoPreview} 
                  alt="Club logo preview" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <Shield className="h-10 w-10 text-dark-800" />
              )}
            </div>
            <input
              id="club-logo-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoChange}
            />
            <button
              type="button"
              onClick={() => document.getElementById('club-logo-input')?.click()}
              className="mt-2 text-sm text-gold-400 hover:underline"
            >
              {logoPreview ? 'Change Logo' : 'Upload Logo'}
            </button>
            {errors.logo && (
              <p className="text-red-500 text-sm mt-1">{errors.logo}</p>
            )}
          </div>
          
          {/* Club Name */}
          <div className="space-y-2">
            <Label htmlFor="club_name" className="text-white">
              Club Name <span className="text-red-500">*</span>
            </Label> 
            <Input
              id="club_name"
              name="club_name"
              value={formData.club_name}
              onChange={handleInputChange}
              className={cn(
                "bg-dark-300 border-dark-400 text-white",
                errors.club_name && "border-red-500"
              )}
              placeholder="Enter club name"
            />
            {errors.club_name && (
              <p className="text-red-500 text-sm">{errors.club_name}</p>
            )}
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Tell us about your club"
              className="bg-dark-300 border-dark-400 text-white min-h-[100px]"
            />
          </div>
          
          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="city" className="text-white">
              City <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-dark-800" />
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={cn(
                  "bg-dark-300 border-dark-400 text-white pl-10",
                  errors.city && "border-red-500"
                )}
                placeholder="Enter city"
              />
            </div>
            {errors.city && (
              <p className="text-red-500 text-sm">{errors.city}</p>
            )}
          </div>
          
          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input 
              id="formatted_address"
              value={formData.formatted_address}
              onChange={(e) => setFormData({ ...formData, formatted_address: e.target.value })}
              placeholder="Enter club address"
              className={cn("bg-dark-300 border-dark-400 text-white", errors.formatted_address && "border-red-500")}
            />
            {errors.formatted_address && <p className="text-red-500 text-sm">{errors.formatted_address}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="formatted_phone_number"
              value={formData.formatted_phone_number}
              onChange={(e) => setFormData({ ...formData, formatted_phone_number: e.target.value })}
              placeholder="Enter phone number"
              className={cn("bg-dark-300 border-dark-400 text-white", errors.formatted_phone_number && "border-red-500")}
            />
            {errors.formatted_phone_number && <p className="text-red-500 text-sm">{errors.formatted_phone_number}</p>}
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website" className="text-white">
              Website
            </Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-dark-800" />
              <Input
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className={cn(
                  "bg-dark-300 border-dark-400 text-white pl-10",
                  errors.website && "border-red-500"
                )}
                placeholder="Enter website URL"
              />
            </div>
            {errors.website && (
              <p className="text-red-500 text-sm">{errors.website}</p>
            )}
          </div>

          
          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-dark-400"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gold-400 hover:bg-gold-500 text-dark-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Club'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateClubModal;